import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Component, effect, input, model, output, signal } from '@angular/core';
import { sanitizeFields, validateBlank, validateYearsLimitFromToday, watchFormRecursively } from '../../../shared/utils/form-utils';

import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FormFieldFeedbackPipe } from '../../../pipes/form-field-feedback/form-field-feedback.pipe';
import { InputMaskModule } from 'primeng/inputmask';
import { InputText } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { NgClass } from '@angular/common';
import { PasswordModule } from 'primeng/password';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { User } from '../../../shared/models/user.model';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'app-register-form',
  imports: [
    ButtonModule,
    InputText,
    DatePickerModule,
    PasswordModule,
    InputMaskModule,
    FormFieldFeedbackPipe,
    TooltipModule,
    ReactiveFormsModule,
    NgClass,
    FormsModule,
    ToggleSwitchModule,
    FloatLabelModule
  ],
  templateUrl: './register-form.component.html',
  styleUrl: './register-form.component.scss'
})
export class RegisterFormComponent {
  readonly registerForm = new FormGroup({
    name: new FormControl<string>('', {
      validators: [
        Validators.required,
        validateBlank()
      ]
    }),
    lastName: new FormControl<string>('', {
      validators: [
        Validators.required,
        validateBlank()
      ]
    }),
    email: new FormControl<string>('', {
      validators: [
        Validators.required,
        Validators.email
      ]
    }),
    birthdate: new FormControl<Date>(new Date(), {
      validators: [
        Validators.required,
        validateYearsLimitFromToday(18, false),
        validateYearsLimitFromToday(100, true)
      ]
    }),
    phone: new FormControl<string>('', {
      validators: [
        Validators.required,
        Validators.pattern(/^\(\d{2}\) \d{5}-\d{4}$/)
      ]
    }),
    password: new FormControl<string>('', {
      validators: [
        Validators.required,
        Validators.minLength(8)
      ]
    }),
    confirmPassword: new FormControl<string>('', {
      validators: [
        Validators.required,
        Validators.minLength(8)
      ]
    })
  });

  readonly initialData = input<User | null>(null);
  readonly onSubmit = output<any>();
  readonly enabled = model<boolean>(true);
  readonly isLoading = model.required<boolean>();
  readonly id = uuidv4();


  readonly today = new Date();

  readonly formWatched = watchFormRecursively(this.registerForm);
  readonly isChangePasswordEnabled = signal<boolean>(false);

  constructor(private readonly messageService: MessageService) {
    effect(() => {
      if (this.enabled()) {
        this.registerForm.enable();
      } else {
        this.registerForm.disable();
      }
    });

    effect(() => {
      if (this.initialData()) {
        this.enabled.set(false);
        this.registerForm.patchValue(this.initialData()!);
      } else {
        this.enabled.set(true);
        this.resetForm();
      }
    })

    effect(() => {
      if (this.isChangePasswordEnabled() || !this.initialData()) {
        this.registerForm.get('password')?.setValidators([
          Validators.required,
          Validators.minLength(8)
        ]);
        this.registerForm.get('confirmPassword')?.setValidators([
          Validators.required,
          Validators.minLength(8)
        ]);
        this.registerForm.setValidators(this.passwordMatchValidator());
      } else {
        this.registerForm.get('password')?.clearValidators();
        this.registerForm.get('confirmPassword')?.clearValidators();
        this.registerForm.clearValidators();
      }

      this.registerForm.get('password')?.updateValueAndValidity({ onlySelf: true });
      this.registerForm.get('confirmPassword')?.updateValueAndValidity({ onlySelf: true });
      this.registerForm.updateValueAndValidity();
    });
  }

  resetForm(): void {
    this.registerForm.reset();
    this.isLoading.set(false);
  }

  submitForm(): void {
    this.isLoading.set(true);

    if (this.registerForm.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção',
        detail: 'Preencha todos os campos corretamente!',
        life: 4000
      });

      this.isLoading.set(false);
      return;
    }

    sanitizeFields(this.registerForm);
    this.onSubmit.emit(this.registerForm.value);
  }


  private passwordMatchValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formGroup = control as FormGroup;
      const password = formGroup.get('password');
      const confirmPassword = formGroup.get('confirmPassword');

      if (confirmPassword?.touched && confirmPassword?.value && password?.value !== confirmPassword?.value) {
        return { passwordMismatch: true };
      }

      return null;
    }
  }
}
